import React, { useContext, useEffect, useState } from 'react';

import questionSvg from '@assets/img/icons/question.svg';
import { TextInput } from '@components/admin/Forms';
import { CardCheckbox } from '@components/Forms/CardCheckbox';
import { ErrorMessage } from '@components/Forms/ErrorMessage';
import { DeliveryInstructionReceiverInput } from '@components/utilities/Bookings/DeliveryInstructionReceiverInput';
import { DeliveryInstructionTruckInfoDialog } from '@components/utilities/Bookings/DeliveryInstructionTruckInfoDialog';
import { DeliveryTruckDisplay } from '@components/utilities/Bookings/DeliveryTruckDisplay';
import { DeliveryInstructionContext } from '@components/utilities/Bookings/helpers';
import { InstructionFieldDisplay as FieldDisplay } from '@components/utilities/Bookings/InstructionFieldDisplay';
import { InstructionContactDisplay } from '@components/utilities/Contact/InstructionContactDisplay';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { calendarStrings } from '@data/calendar';
import { instructionStrings } from '@data/instruction';
import { areContactsEqual } from '@models/Contact';
import { DeliveryTruckName } from '@models/DeliveryInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import Color from 'color';
import { useField } from 'formik';

const strings = createStrings({
  ja: {
    receiverChangedAlert: `${bookingInquiryStrings.receiver}が変更されました。`,
    preferredDeliveryDateHint:
      '＊上記以外の日本国内配送方法、納品希望日、納品時間を指定する場合、追加料金が発生する場合があります。',
    specialNoteHint: () => (
      <>
        ＊特記事項ありの場合、追加料金が発生いたします。
        <br />
        料金の詳細については別途担当者よりご連絡いたします。
        <br />
      </>
    ),
    specialNotePlaceholder:
      '特記事項ありを選択した場合、こちらのご入力は必須です。\n例：商品の一辺のサイズが2メートル以上・1個の商品重量が1000KGS以上・車両の種類（ユニック車・平車）など。',
    truckOptionsHint: () => (
      <>
        ＊荷物のお渡し方法は、<strong>「車上渡し」</strong>
        です。
        <br />
        <em>
          &nbsp;&nbsp;&nbsp;車上渡しとは、トラックの荷台上からお客様ご自身で荷降ろしをして頂く方法です。
        </em>
        <br />
        <em>
          &nbsp;&nbsp;&nbsp;ドライバーによる階上への運搬などのお手伝いはできませんのでご了承ください。
        </em>
        <br />
        ＊車上渡し以外のお渡し方法をご希望される場合には必ず下記
        <em>「国内トラック配送に関する特記事項あり」</em>を選択し、事前にご相談ください。
        <br />
        ＊別途手数料が発生する可能性もございます。
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  displayValueContainer: {
    ...common.flexColumn,
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    rowGap: '0.5rem',
  },
  hint: {
    ...common.textGray700,
    ...common.smallerText,
    '& strong': {
      ...common.textPrimary,
      ...common.fontWeight600,
    },
    '& em': {
      ...common.textError,
      ...common.fontWeight600,
    },
  },
  truckOptionDisplay: {
    ...common.backgroundPrimaryAlpha3,
    ...common.flexColumn,
    padding: '1rem',
  },
  truckOptionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, auto)',
  },
  truckOptionGridHead: {
    ...common.textGray700,
    ...common.middleText,
    marginBottom: '0.5rem',
  },
  truckOptionGridData: {
    ...common.textBlack,
    ...common.normalText,
    ...common.fontWeight600Important,
  },
  memoDisplay: {
    ...common.textGray700,
    ...common.middleText,
    borderTop: `1px dashed ${Color(theme.palette.primary.main).alpha(0.5).string()}`,
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
  },
  receiverChangedAlert: {
    ...common.middleText,
    color: theme.palette.warning.main,
    fontWeight: 'bold',
  },
  specialNoteOption: {
    ...common.flexColumn,
    justifyContent: 'center',
    paddingLeft: 40,
    width: '100%',
    height: '100%',
  },
}));

export const DeliveryInstructionInput: React.FC = () => {
  const { isEditing, deliveryInstruction } = useContext(DeliveryInstructionContext);

  const [truckInfoOpen, setTruckInfoOpen] = useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [truckField] = useField<DeliveryTruckName>('truck');
  const [preferredDeliveryDateField, , preferredDeliveryDateHelper] =
    useField<string>('preferred_delivery_date');
  const [preferredDeliveryTimeField, , preferredDeliveryTimeHelper] =
    useField<string>('preferred_delivery_time');
  const [hasSpecialNoteField, , hasSpecialNoteHelper] = useField<boolean>('has_special_note');

  const shouldShowDeliveryDate = truckField.value && truckField.value !== 'その他';
  const shouldShowDeliveryTime = truckField.value && truckField.value !== 'その他';

  const currentTruckOption = deliveryInstruction.truck_options.find(
    (t) => t.name === truckField.value,
  );

  useEffect(() => {
    if (
      (truckField.value === 'その他' ||
        (truckField.value !== '' &&
          (preferredDeliveryDateField.value === '' ||
            preferredDeliveryTimeField.value === 'その他'))) &&
      hasSpecialNoteField.value === false &&
      isEditing
    ) {
      hasSpecialNoteHelper.setValue(true);
    }
  }, [
    truckField.value,
    preferredDeliveryDateField.value,
    preferredDeliveryTimeField.value,
    isEditing,
  ]);

  return (
    <>
      <DeliveryInstructionTruckInfoDialog
        open={truckInfoOpen}
        onClose={() => setTruckInfoOpen(false)}
      />

      <FieldDisplay
        required={isEditing}
        label={bookingInquiryStrings.receiver}
        value={
          <div className={classes.displayValueContainer}>
            {isEditing ? (
              <DeliveryInstructionReceiverInput />
            ) : (
              <>
                <InstructionContactDisplay
                  contact={deliveryInstruction.receiver || deliveryInstruction.old_receiver}
                />

                {!!deliveryInstruction.receiver &&
                  !areContactsEqual(
                    deliveryInstruction.receiver,
                    deliveryInstruction.old_receiver,
                  ) && (
                    <>
                      <div className={classes.receiverChangedAlert}>
                        {strings.receiverChangedAlert}
                      </div>
                      <InstructionContactDisplay contact={deliveryInstruction.old_receiver} stale />
                    </>
                  )}
              </>
            )}
          </div>
        }
      />

      <FieldDisplay
        required={isEditing}
        label={
          <>
            {instructionStrings.truckChoice}{' '}
            {isEditing && (
              <a onClick={() => setTruckInfoOpen(true)} style={{ cursor: 'pointer' }}>
                <img src={questionSvg} alt="Questions" />
              </a>
            )}
          </>
        }
        value={
          <div className={classes.displayValueContainer}>
            {isEditing ? (
              <>
                {deliveryInstruction.truck_options.map((item) => (
                  <CardCheckbox
                    name="truck"
                    key={item.name}
                    label={<DeliveryTruckDisplay item={item} />}
                    value={item.name}
                    onSelected={() => {
                      const nextPreferredDeliveryDateDate = (
                        deliveryInstruction.delivery_date_options[item.name] ?? []
                      ).find((d) => d === preferredDeliveryDateField.value);

                      preferredDeliveryDateHelper.setValue(
                        nextPreferredDeliveryDateDate ??
                          deliveryInstruction.delivery_date_options[item.name]?.[0] ??
                          '',
                      );

                      const nextPreferredDeliveryTime = instructionStrings.deliveryTimeOptions[
                        item.name
                      ].find((d) => d.value === preferredDeliveryTimeField.value)?.value;

                      preferredDeliveryTimeHelper.setValue(
                        nextPreferredDeliveryTime ??
                          instructionStrings.deliveryTimeOptions[item.name][0].value ??
                          'その他',
                      );
                    }}
                  />
                ))}
                <ErrorMessage name="truck" />
                <div className={classes.hint}>{strings.truckOptionsHint()}</div>
              </>
            ) : (
              <>
                <div className={classes.truckOptionDisplay}>
                  <div className={classes.truckOptionGrid}>
                    <div className={classes.truckOptionGridHead}>{instructionStrings.truck}</div>
                    <div className={classes.truckOptionGridHead}>
                      {instructionStrings.preferredDeliveryDate}
                    </div>
                    <div className={classes.truckOptionGridHead}>
                      {instructionStrings.deliveryTime}
                    </div>

                    <div className={classes.truckOptionGridData}>
                      {currentTruckOption?.name
                        ? instructionStrings[currentTruckOption?.name]
                        : '-'}
                    </div>
                    <div className={classes.truckOptionGridData}>
                      {deliveryInstruction.preferred_delivery_date
                        ? calendarStrings.yearMonthDay(
                            new Date(deliveryInstruction.preferred_delivery_date),
                          )
                        : '-'}
                    </div>
                    <div className={classes.truckOptionGridData}>
                      {deliveryInstruction.preferred_delivery_time}
                    </div>
                  </div>

                  <div className={classes.memoDisplay}>
                    {instructionStrings.specialNote}:{' '}
                    {deliveryInstruction.has_special_note
                      ? deliveryInstruction.special_note || formStrings.exist
                      : '-'}
                  </div>
                </div>

                <div className={classes.hint}>{strings.specialNoteHint()}</div>
              </>
            )}
          </div>
        }
      />
      {shouldShowDeliveryDate && isEditing && (
        <FieldDisplay
          required
          label={instructionStrings.preferredDeliveryDate}
          value={
            <div className={classes.displayValueContainer}>
              <TextInput
                name="preferred_delivery_date"
                noLabel
                choose
                options={(deliveryInstruction.delivery_date_options[truckField.value] ?? [])
                  .map((date) => ({
                    value: date,
                    label: calendarStrings.yearMonthDay(new Date(date)),
                  }))
                  .concat([{ value: '', label: instructionStrings.deliveryDateMemo }])}
                formClasses={{
                  root: classes.flex1,
                }}
              />
            </div>
          }
        />
      )}

      {shouldShowDeliveryTime && isEditing && (
        <FieldDisplay
          required
          label={instructionStrings.deliveryTime}
          value={
            <TextInput
              name="preferred_delivery_time"
              noLabel
              formClasses={{
                root: classes.flex1,
              }}
              choose
              options={instructionStrings.deliveryTimeOptions[truckField.value]}
            />
          }
        />
      )}

      {isEditing && (
        <FieldDisplay
          label={instructionStrings.specialNote}
          required
          value={
            <div className={classes.displayValueContainer}>
              <CardCheckbox
                name="has_special_note"
                label={
                  <span className={classes.specialNoteOption}>
                    {instructionStrings.deliveryInstructionHasSpecialNoteFalse}
                  </span>
                }
                value={false}
                size="small"
              />
              <CardCheckbox
                name="has_special_note"
                label={
                  <span className={classes.specialNoteOption}>
                    {instructionStrings.deliveryInstructionHasSpecialNoteTrue}
                  </span>
                }
                value={true}
                size="small"
              />
              <ErrorMessage name="has_special_note" />
            </div>
          }
        />
      )}

      {hasSpecialNoteField.value && isEditing && (
        <FieldDisplay
          label={instructionStrings.specialNoteDetails}
          required
          value={
            <div className={classes.displayValueContainer}>
              <TextInput
                name="special_note"
                noLabel
                multiline
                formClasses={{
                  root: classes.flex1,
                }}
                placeholder={strings.specialNotePlaceholder}
              />
              <div className={classes.hint}>{strings.specialNoteHint()}</div>
            </div>
          }
        />
      )}
    </>
  );
};
